import Link from 'next/link';
import { CSSObject, Theme } from '@emotion/react';
import styled from '@emotion/styled';

import { landingColors, landingLayout, landingRadius } from '@/features/landing/lib/landing-tokens';

import { breakpoints } from '@/shared/design/media';
import { commonColors } from '@/shared/design/tokens/themes';
import { fontWeight, headingsNew, paragraphsNew } from '@/shared/design/tokens/typography';

export const Page = styled.div({
  width: `calc(100% - ${landingLayout.sidePaddingMobile * 2}px)`,
  maxWidth: landingLayout.maxWidth - landingLayout.sidePadding * 2,
  margin: '40px auto 96px',
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',

  [`@media (max-width:${breakpoints.mobile}px)`]: {
    maxWidth: '500px',
    margin: '20px auto 110px',
  },
});

export const Hero = styled.section((props) => ({
  position: 'relative',
  minHeight: '560px',
  display: 'flex',
  alignItems: 'center',
  overflow: 'hidden',
  padding: '56px',
  borderRadius: landingRadius.card,
  backgroundColor: props.theme.colors.background.secondary,

  [`@media (max-width:${breakpoints.tablet}px)`]: {
    minHeight: '500px',
    padding: '40px',
  },

  [`@media (max-width:${breakpoints.mobile}px)`]: {
    minHeight: 'auto',
    alignItems: 'stretch',
    flexDirection: 'column',
    padding: 0,
    borderRadius: '20px',
  },
}));

export const HeroContent = styled.div({
  position: 'relative',
  zIndex: 1,
  width: '100%',
  maxWidth: '700px',
  display: 'flex',
  flexDirection: 'column',
  gap: '28px',

  [`@media (max-width:${breakpoints.mobile}px)`]: {
    maxWidth: 'none',
    order: 1,
    gap: '20px',
    padding: '24px',
  },
});

export const HeroMedia = styled.div((props) => ({
  position: 'absolute',
  zIndex: 0,
  inset: 0,

  '&::after': {
    position: 'absolute',
    zIndex: 1,
    inset: 0,
    content: '""',
    background: `linear-gradient(90deg, ${props.theme.colors.background.secondary} 0%, ${props.theme.colors.background.secondary} 34%, rgba(42, 43, 48, 0.72) 58%, rgba(42, 43, 48, 0.16) 78%, rgba(42, 43, 48, 0) 100%)`,
  },

  [`@media (max-width:${breakpoints.mobile}px)`]: {
    position: 'relative',
    inset: 'auto',
    order: 2,
    width: '100%',
    aspectRatio: '1200 / 630',

    '&::after': {
      display: 'none',
    },
  },
}));

export const HeroImage = styled.img({
  position: 'absolute',
  zIndex: 0,
  inset: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  objectPosition: 'center',

  [`@media (max-width:${breakpoints.tablet}px)`]: {
    objectPosition: '58% center',
  },

  [`@media (max-width:${breakpoints.mobile}px)`]: {
    objectPosition: 'center',
  },
});

export const RelatedNav = styled.nav((props) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '10px',
  padding: '24px',
  borderRadius: '24px',
  background: props.theme.colors.background.secondary,

  a: {
    padding: '9px 14px',
    border: `1px solid ${commonColors.violet}`,
    borderRadius: '999px',
    color: props.theme.colors.text.primary,
    textDecoration: 'none',
    fontWeight: fontWeight.medium,
  },
}));

export const EyebrowRow = styled.div({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '10px',
});

type PillTone = 'accent' | 'dark' | 'light';

const pillStyles = (props: { $tone?: PillTone; theme: Theme }): CSSObject => {
  const tone = props.$tone ?? 'accent';
  const palette = {
    accent: {
      background: commonColors.violet,
      border: commonColors.violet,
      color: commonColors.white,
    },
    dark: {
      background: commonColors.darkGrey,
      border: commonColors.darkGrey,
      color: commonColors.white,
    },
    light: {
      background: props.theme.colors.background.secondary,
      border: commonColors.lightGrey,
      color: props.theme.colors.text.primary,
    },
  }[tone];

  return {
    ...paragraphsNew.s,
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: '40px',
    padding: '0 18px',
    border: `1px solid ${palette.border}`,
    borderRadius: landingRadius.pill,
    backgroundColor: palette.background,
    color: palette.color,
    fontWeight: fontWeight.medium,
  };
};

export const Pill = styled.span<{ $tone?: PillTone }>(pillStyles);

/** A light pill that leads to a page, e.g. the League division of a rated tournament. */
export const PillLink = styled(Link)((props) => ({
  ...pillStyles({ $tone: 'light', theme: props.theme }),
  textDecoration: 'none',

  '&:hover': { borderColor: commonColors.violet },
}));

/** A team name that leads to the team's League page. */
export const TeamLink = styled(Link)({
  color: 'inherit',
  textDecorationColor: commonColors.violet,
  textUnderlineOffset: '3px',

  '&:hover': { color: commonColors.violet },
});

export const Title = styled.h1((props) => ({
  fontSize: '60px',
  lineHeight: 1.05,
  fontWeight: 500,
  width: '100%',
  maxWidth: '760px',
  color: props.theme.colors.text.primary,

  [`@media (max-width:${breakpoints.tablet}px)`]: {
    fontSize: '44px',
  },

  [`@media (max-width:${breakpoints.mobile}px)`]: {
    fontSize: '32px',
  },
}));

export const Summary = styled.p((props) => ({
  ...paragraphsNew.base,
  width: '100%',
  maxWidth: '690px',
  color: props.theme.colors.text.primary,

  [`@media (max-width:${breakpoints.tablet}px)`]: {
    ...paragraphsNew.base_adaptive,
  },

  [`@media (max-width:${breakpoints.mobile}px)`]: {
    ...paragraphsNew.base_mobile,
  },
}));

export const HeroActions = styled.div({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '12px',
});

export const ActionLink = styled.a<{ $variant?: 'primary' | 'secondary' }>((props) => ({
  ...paragraphsNew.base_adaptive,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '48px',
  padding: '0 28px',
  border: `1px solid ${commonColors.violet}`,
  borderRadius: landingRadius.pill,
  backgroundColor: props.$variant === 'secondary' ? props.theme.colors.background.secondary : commonColors.violet,
  color: props.$variant === 'secondary' ? props.theme.colors.text.primary : commonColors.white,
  fontWeight: fontWeight.medium,
  textDecoration: 'none',
  transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out',

  '&:focus-visible': { outline: `3px solid ${landingColors.violetOnDark}`, outlineOffset: 4 },

  '&:hover': {
    backgroundColor: props.$variant === 'secondary' ? commonColors.violet : commonColors.violet_hover,
    color: commonColors.white,
  },

  [`@media (max-width:${breakpoints.mobile}px)`]: {
    width: '100%',
    minHeight: '50px',
    ...paragraphsNew.base_mobile,
    fontWeight: fontWeight.medium,
  },
}));

export const StatGrid = styled.div({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
  gap: '20px',

  [`@media (max-width:${breakpoints.tablet}px)`]: {
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  },

  [`@media (max-width:${breakpoints.mobile}px)`]: {
    gridTemplateColumns: '1fr',
    gap: '10px',
  },
});

export const StatCard = styled.div((props) => ({
  minHeight: '148px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: '12px',
  padding: '24px',
  borderRadius: landingRadius.card,
  backgroundColor: props.theme.colors.background.secondary,
  border: `1px solid ${props.theme.colors.background.primary}`,

  [`@media (max-width:${breakpoints.mobile}px)`]: {
    minHeight: '112px',
    padding: '20px',
    borderRadius: '20px',
  },
}));

export const StatLabel = styled.div((props) => ({
  ...paragraphsNew.s,
  color: props.theme.colors.text.primary,
  opacity: 0.72,
}));

export const StatValue = styled.div((props) => ({
  ...paragraphsNew.base_adaptive,
  color: props.theme.colors.text.primary,
  fontWeight: fontWeight.medium,

  [`@media (max-width:${breakpoints.mobile}px)`]: {
    ...paragraphsNew.base_mobile,
    fontWeight: fontWeight.medium,
  },
}));

export const Section = styled.section((props) => ({
  padding: '40px',
  borderRadius: landingRadius.card,
  backgroundColor: props.theme.colors.background.secondary,
  color: props.theme.colors.text.primary,

  [`@media (max-width:${breakpoints.mobile}px)`]: {
    padding: '24px',
    borderRadius: '20px',
  },
}));

export const SectionHeader = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  gap: '16px',
  alignItems: 'flex-start',
  marginBottom: '24px',

  h2: {
    marginBottom: 0,
  },

  [`@media (max-width:${breakpoints.mobile}px)`]: {
    flexDirection: 'column',
    marginBottom: '20px',
  },
});

export const SectionTitle = styled.h2((props) => ({
  ...headingsNew.heading2,
  color: props.theme.colors.text.primary,
  marginBottom: '24px',

  [`@media (max-width:${breakpoints.mobile}px)`]: {
    ...headingsNew.heading2_adaptive,
    marginBottom: '16px',
  },
}));

export const Text = styled.p((props) => ({
  ...paragraphsNew.base_adaptive,
  color: props.theme.colors.text.primary,
  whiteSpace: 'pre-line',

  [`@media (max-width:${breakpoints.mobile}px)`]: {
    ...paragraphsNew.base_mobile,
  },
}));

export const CardGrid = styled.div({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: '20px',

  [`@media (max-width:${breakpoints.tablet}px)`]: {
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  },

  [`@media (max-width:${breakpoints.mobile}px)`]: {
    gridTemplateColumns: '1fr',
    gap: '10px',
  },
});

export const SmallCard = styled.div((props) => ({
  minHeight: '128px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: '8px',
  padding: '24px',
  borderRadius: landingRadius.card,
  backgroundColor: props.theme.colors.hover_card_color,

  [`@media (max-width:${breakpoints.mobile}px)`]: {
    minHeight: '108px',
    padding: '20px',
    borderRadius: '20px',
  },
}));

export const SmallTitle = styled.h3((props) => ({
  ...paragraphsNew.base_adaptive,
  color: props.theme.colors.text.primary,
  fontWeight: fontWeight.medium,

  [`@media (max-width:${breakpoints.mobile}px)`]: {
    ...paragraphsNew.base_mobile,
    fontWeight: fontWeight.medium,
  },
}));

export const SmallText = styled.p((props) => ({
  ...paragraphsNew.s,
  color: props.theme.colors.text.primary,
  opacity: 0.72,

  [`@media (max-width:${breakpoints.mobile}px)`]: {
    ...paragraphsNew.s_adaptive,
  },
}));

export const TableWrap = styled.div({
  overflowX: 'auto',
});

export const Table = styled.table((props) => ({
  width: '100%',
  minWidth: '640px',
  borderCollapse: 'collapse',

  'th, td': {
    ...paragraphsNew.s,
    padding: '16px 12px',
    textAlign: 'left',
    borderBottom: `1px solid ${props.theme.colors.background.primary}`,
  },

  th: {
    color: commonColors.gray2,
    fontWeight: fontWeight.medium,
  },

  td: {
    color: props.theme.colors.text.primary,
  },
}));

export const VenueGrid = styled.div({
  display: 'grid',
  gridTemplateColumns: 'minmax(280px, 0.65fr) minmax(0, 1.35fr)',
  gap: '20px',
  alignItems: 'start',

  [`@media (max-width:${breakpoints.tablet}px)`]: {
    gridTemplateColumns: '1fr',
  },
});

export const VenueInfo = styled(SmallCard)({
  width: '100%',
  minHeight: 'auto',
  justifyContent: 'flex-start',
});

export const VenueMapBox = styled.div({
  width: '100%',

  '&& > div': {
    maxWidth: '100%',
    minHeight: '520px',
    borderRadius: landingRadius.card,
  },

  [`@media (max-width:${breakpoints.mobile}px)`]: {
    '&& > div': {
      minHeight: '360px',
      borderRadius: '20px',
    },
  },
});
